<template>
  <div class="flex flex-col gap-6 proxima_nova">
    <div class="flex flex-row justify-between items-center">
      <div class="flex flex-row gap-2 items-center">
        <span class="text-[18px] font-semibold text_color">Currency</span>
        <a-tooltip>
          <template #title>
            <span
              >Exchange rates are updated every month. Closed deals are updated
              with the exchange rate at the time of closing.</span
            >
          </template>
          <ThemeImage :icon="imageNames.INFO" alt="info_icon" class="size-4" />
        </a-tooltip>
      </div>
      <a-button
        class="primary_medium cursor-pointer"
        @click="showAddCurrencyModal"
        >Add Currency</a-button
      >
    </div>

    <CustomTable
      v-if="currenciesData.length > 0"
      :headers="currenciesHeaders"
      :data="currenciesData"
      source="currency"
      :show-actions-column="true"
      :actions="actionOptions"
      :identifier-key="'currency'"
      :custom-column-render="customColumnRender"
      :is-more-options-disabled="'is_primary'"
      height="calc(100vh - (275px + var(--banner-height)))"
    />

    <div v-else class="flex flex-col items-center justify-center gap-2 mt-20">
      <ThemeImage
        :icon="imageNames.EMPTY_SCREEN_GREY"
        alt="empty_icon"
        class="size-20"
      />
      <span class="text-base secondary_color text_color"
        >No currencies added yet!</span
      >
    </div>
  </div>

  <CustomModal
    v-model:visible="isAddCurrencyModal"
    title="Add Currency"
    width="32vw"
  >
    <div class="flex flex-col gap-2">
      <span class="text-[13px] secondary_color text_color">Currency</span>
      <a-select
        v-model:value="currency"
        placeholder="Select currency"
        show-search
        :filter-option="filterOption"
      >
        <template #suffixIcon>
          <ThemeImage
            :icon="imageNames.BOTTOM_ARROW"
            alt="bottom_arrow"
            class="size-4"
          />
        </template>
        <a-select-option
          v-for="currency in currencies"
          :key="currency.value"
          :value="currency.value"
          :label="currency.label"
        >
          {{ currency.label }}
        </a-select-option>
      </a-select>
    </div>
    <template #footer>
      <div class="flex flex-row items-center gap-2">
        <a-button
          class="secondary_medium cursor-pointer"
          @click="isAddCurrencyModal = false"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium cursor-pointer"
          :loading="isLoading"
          @click="addCurrency"
          >Add Currency</a-button
        >
      </div>
    </template>
  </CustomModal>

  <CustomModal
    v-model:visible="isDeleteCurrencyModal"
    :title="`Delete ${currency} ?`"
    width="32vw"
  >
    <span class="text-sm text_color"
      >Deleting a currency will remove it as an option on new deals. Existing
      deals will keep any deleted currencies.</span
    >
    <template #footer>
      <div class="flex flex-row items-center gap-2">
        <a-button
          class="secondary_medium cursor-pointer"
          @click="isDeleteCurrencyModal = false"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium cursor-pointer"
          :loading="isLoading"
          @click="deleteCurrency"
          >Delete Currency</a-button
        >
      </div>
    </template>
  </CustomModal>

  <CustomModal
    v-model:visible="isMarkAsPrimaryModal"
    :title="`Mark ${currency} as Primary?`"
    width="32vw"
  >
    <span class="text-sm text_color"
      >Are you sure you want to mark this currency as primary?</span
    >
    <template #footer>
      <div class="flex flex-row items-center gap-2">
        <a-button
          class="secondary_medium cursor-pointer"
          @click="isMarkAsPrimaryModal = false"
          >Cancel</a-button
        >
        <a-button
          class="primary_medium cursor-pointer"
          :loading="isLoading"
          @click="markAsPrimary"
          >Mark as Primary</a-button
        >
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import CustomAlerts from "@/src/components/CustomAlerts.vue";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { ref, onMounted } from "vue";
import { currenciesHeaders } from "@/src/constants/currencyConstants.js";
import CustomModal from "@/src/components/CustomModal.vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import { filterOption } from "@/src/utils/utils.js";
import CurrencyTag from "@/src/components/currency_components/CurrencyTag.vue";

const isAddCurrencyModal = ref(false);
const isDeleteCurrencyModal = ref(false);
const currency = ref(null);
const currencyIdToDelete = ref(null);
const currencies = ref([]);
const currenciesData = ref([]);
const isLoading = ref(false);
const isMarkAsPrimaryModal = ref(false);
const currencyIdToMarkAsPrimary = ref(null);

const actionOptions = [
  {
    label: "Mark as primary",
    handler: (row) => markAsPrimaryConfirm(row),
    icon: imageNames.STAR_GREY,
  },
  {
    label: "Delete",
    handler: (row) => deleteCurrencyConfirm(row),
    icon: imageNames.DELETE_GREY,
  },
];

const customColumnRender = {
  currency_name: {
    component: CurrencyTag,
    props: (row) => ({
      row: row,
    }),
  },
};

const loadAllCurrencies = async () => {
  try {
    const response = await DashboardService.getAllCurrencies();
    if (response?.success && response?.currencies?.length > 0) {
      currencies.value = response.currencies.map((curr) => ({
        value: curr.id,
        label: `${curr.name} (${curr.code} ${curr.symbol})`,
        code: curr.code,
        name: curr.name,
        symbol: curr.symbol,
      }));

      const usdCurrency = currencies.value.find((curr) => curr.code === "USD");
      if (usdCurrency && !currency.value) {
        currency.value = usdCurrency.value;
      }
    }
  } catch (error) {
    console.error("Error loading currencies:", error);
    message.error("Failed to load currencies");
  }
};

const loadWorkspaceCurrencies = async () => {
  try {
    isLoading.value = true;
    const response = await DashboardService.getWorkspaceCurrencies();
    if (response?.success && response?.currencies?.length > 0) {
      currenciesData.value = response.currencies.map((curr) => ({
        id: curr.id,
        currency: curr.currency_code,
        format: `${curr.currency_symbol}123,432`,
        last_updated: new Date(curr.updated_at).toLocaleDateString(),
        currency_name: `${curr.currency_name} (${curr.currency_code} ${curr.currency_symbol})`,
        currency_symbol: curr.currency_symbol,
        is_primary: curr.is_primary,
      }));
    } else {
      currenciesData.value = [];
    }
  } catch (error) {
    console.error("Error loading workspace currencies:", error);
    message.error("Failed to load workspace currencies");
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await Promise.all([loadAllCurrencies(), loadWorkspaceCurrencies()]);
});

const markAsPrimaryConfirm = async (row) => {
  isMarkAsPrimaryModal.value = true;
  currency.value = row.currency_name;
  currencyIdToMarkAsPrimary.value = row.id;
};

const markAsPrimary = async () => {
  try {
    isLoading.value = true;
    const params = {
      is_primary: true,
    };
    const response = await DashboardService.markCurrencyAsPrimary(
      params,
      currencyIdToMarkAsPrimary.value,
    );
    if (response?.success) {
      message.success("Currency marked as primary successfully");
      isMarkAsPrimaryModal.value = false;
      await loadWorkspaceCurrencies();
    } else if (response?.error) {
      message.error(response.error);
    } else {
      message.error("Failed to mark currency as primary");
    }
  } catch (error) {
    console.error("Error marking currency as primary:", error);
    const errorMessage =
      error?.response?.data?.error || "Failed to mark currency as primary";
    message.error(errorMessage);
  } finally {
    isLoading.value = false;
    currencyIdToMarkAsPrimary.value = null;
  }
};

const deleteCurrencyConfirm = async (row) => {
  isDeleteCurrencyModal.value = true;
  currency.value = row.currency_name;
  currencyIdToDelete.value = row.id;
};

const deleteCurrency = async () => {
  try {
    isLoading.value = true;
    const response = await DashboardService.deleteWorkspaceCurrency(
      currencyIdToDelete.value,
    );
    if (response?.success) {
      message.success("Currency deleted successfully");
      await loadWorkspaceCurrencies();
    } else if (response?.error) {
      message.error(response.error);
    } else {
      message.error("Failed to delete currency");
    }
  } catch (error) {
    console.error("Error deleting currency:", error);
    const errorMessage =
      error?.response?.data?.error || "Failed to delete currency";
    message.error(errorMessage);
  } finally {
    isDeleteCurrencyModal.value = false;
    isLoading.value = false;
    currencyIdToDelete.value = null;
  }
};

const showAddCurrencyModal = () => {
  const usdCurrency = currencies.value.find((curr) => curr.code === "USD");
  if (usdCurrency) {
    currency.value = usdCurrency.value;
  }
  isAddCurrencyModal.value = true;
};

const addCurrency = async () => {
  try {
    isLoading.value = true;
    if (!currency.value) {
      message.error("Please select a currency");
      return;
    }

    const params = {
      currency_id: currency.value,
      is_primary: false,
    };

    const response = await DashboardService.CreateWorkspaceCurrency(params);
    if (response?.success) {
      message.success("Currency added successfully");
      isAddCurrencyModal.value = false;
      await loadWorkspaceCurrencies();
    } else if (response?.error) {
      message.error(response.error);
    } else {
      message.error("Failed to add currency");
    }
  } catch (error) {
    console.error("Error adding currency:", error);
    const errorMessage =
      error?.response?.data?.error || "Failed to add currency";
    message.error(errorMessage);
  } finally {
    isLoading.value = false;
  }
};
</script>

<style scoped></style>
